import React, { createContext, useContext, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { app, db } from '../components/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { userProfileImageService } from '../services/userProfileImageService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser]     = useState(null);
  const [userId, setUserId]               = useState(null);
  const [userEmail, setUserEmail]         = useState(null);
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [userRoles, setUserRoles]         = useState([]);
  const [loading, setLoading]             = useState(true);

  // Profile Image State
  const [profileImage, setProfileImage]                 = useState(null);
  const [profileImageBlobUrl, setProfileImageBlobUrl]   = useState(null);
  const [profileImageLoading, setProfileImageLoading]   = useState(false);
  const currentBlobUrlRef = useRef(null);

  useEffect(() => {
    const auth = getAuth(app);

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        setUserId(user.uid);
        setUserEmail(user.email);
        setEmailVerified(user.emailVerified);
        console.log("Auth context updated - User ID:", user.uid);
        console.log("Auth context updated - Email:", user.email);
        console.log("Auth context updated - Email Verified:", user.emailVerified);

        // Read roles from Firebase custom claims
        try {
          const tokenResult = await user.getIdTokenResult();
          const roles = tokenResult.claims?.roles || [];
          setUserRoles(Array.isArray(roles) ? roles : [roles]);
          console.log("Auth context updated - Roles:", roles);
        } catch (err) {
          console.warn('Could not read roles from token claims:', err.message);
          setUserRoles([]);
        }

        // Check if phone is linked directly on the Firebase User object
        const hasPhoneProvider = user.providerData && user.providerData.some(provider => provider.providerId === 'phone');
        const hasPhoneNumber = !!user.phoneNumber;

        if (hasPhoneNumber || hasPhoneProvider) {
          setPhoneVerified(true);
          console.log("Auth context updated - Phone Verified via Firebase Provider: true");
        } else {
          // Fallback to Firestore
          try {
            const userDoc = await getDoc(doc(db, 'users', user.uid));
            if (userDoc.exists() && userDoc.data().phoneVerified === true) {
              setPhoneVerified(true);
              console.log("Auth context updated - Phone Verified via Firestore: true");
            } else {
              setPhoneVerified(false);
            }
          } catch (err) {
            console.warn('Could not read phoneVerified from Firestore:', err.message);
            setPhoneVerified(false);
          }
        }

        // Fetch user profile image on login
        fetchProfileImage(user.uid);
      } else {
        setCurrentUser(null);
        setUserId(null);
        setUserEmail(null);
        setEmailVerified(false);
        setPhoneVerified(false);
        setUserRoles([]);
        clearProfileImage();
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const clearProfileImage = useCallback(() => {
    if (currentBlobUrlRef.current && currentBlobUrlRef.current.startsWith('blob:')) {
      window.URL.revokeObjectURL(currentBlobUrlRef.current);
    }
    currentBlobUrlRef.current = null;
    setProfileImage(null);
    setProfileImageBlobUrl(null);
    setProfileImageLoading(false);
  }, []);

  const updateProfileImageState = useCallback((imageData, blobUrl) => {
    if (currentBlobUrlRef.current && currentBlobUrlRef.current !== blobUrl && currentBlobUrlRef.current.startsWith('blob:')) {
      window.URL.revokeObjectURL(currentBlobUrlRef.current);
    }
    currentBlobUrlRef.current = blobUrl || null;
    setProfileImage(imageData || null);
    setProfileImageBlobUrl(blobUrl || null);
  }, []);

  const fetchProfileImage = useCallback(async (targetUserId) => {
    const auth = getAuth(app);
    const uid = targetUserId || auth.currentUser?.uid;
    if (!uid) {
      clearProfileImage();
      return null;
    }

    setProfileImageLoading(true);
    try {
      const res = await userProfileImageService.getProfileImage(uid);
      if (res.success && res.data && res.data.profileImageUrl) {
        setProfileImage(res.data);
        const blobRes = await userProfileImageService.getImageBlobUrl(res.data.profileImageUrl);
        if (blobRes.success && blobRes.url) {
          if (currentBlobUrlRef.current && currentBlobUrlRef.current !== blobRes.url && currentBlobUrlRef.current.startsWith('blob:')) {
            window.URL.revokeObjectURL(currentBlobUrlRef.current);
          }
          currentBlobUrlRef.current = blobRes.url;
          setProfileImageBlobUrl(blobRes.url);
          return { data: res.data, url: blobRes.url };
        } else {
          setProfileImageBlobUrl(null);
          return { data: res.data, url: null };
        }
      } else {
        clearProfileImage();
        return null;
      }
    } catch (err) {
      console.warn('Could not fetch user profile image:', err.message);
      clearProfileImage();
      return null;
    } finally {
      setProfileImageLoading(false);
    }
  }, [clearProfileImage]);

  // Reload the current user from Firebase to get the latest emailVerified status
  const reloadUser = async () => {
    try {
      const auth = getAuth(app);
      if (auth.currentUser) {
        await auth.currentUser.reload();
        const refreshed = auth.currentUser;
        setCurrentUser({ ...refreshed });
        setEmailVerified(refreshed.emailVerified);
        return refreshed.emailVerified;
      }
      return false;
    } catch (error) {
      console.error("reloadUser error:", error);
      return false;
    }
  };

  // Force-refresh the Firebase ID token and update userRoles from custom claims.
  // Call this after the backend assigns COURSE_CREATOR role so the UI updates immediately.
  const refreshUserRoles = async () => {
    try {
      const auth = getAuth(app);
      if (auth.currentUser) {
        const tokenResult = await auth.currentUser.getIdTokenResult(true); // force-refresh
        const roles = tokenResult.claims?.roles || [];
        const normalizedRoles = Array.isArray(roles) ? roles : [roles];
        setUserRoles(normalizedRoles);
        console.log('Roles refreshed from token claims:', normalizedRoles);
        return normalizedRoles;
      }
      return [];
    } catch (error) {
      console.error('refreshUserRoles error:', error);
      return [];
    }
  };

  // Write phoneVerified: true to Firestore and update local state
  const setPhoneVerifiedInDb = async (uid) => {
    try {
      await setDoc(doc(db, 'users', uid), { phoneVerified: true }, { merge: true });
      setPhoneVerified(true);
      console.log("Phone verified flag saved to Firestore for uid:", uid);
    } catch (error) {
      console.error('setPhoneVerifiedInDb error:', error);
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      const auth = getAuth(app);
      await signOut(auth);
      setPhoneVerified(false);
      clearProfileImage();
      console.log("User logged out successfully");
    } catch (error) {
      console.error("Logout error:", error);
      throw error;
    }
  };

  const refreshProfileImage = useCallback(() => {
    const auth = getAuth(app);
    return fetchProfileImage(auth.currentUser?.uid);
  }, [fetchProfileImage]);

  const value = useMemo(() => ({
    currentUser,
    userId,
    userEmail,
    emailVerified,
    phoneVerified,
    userRoles,
    loading,
    profileImage,
    profileImageBlobUrl,
    profileImageLoading,
    fetchProfileImage,
    refreshProfileImage,
    updateProfileImageState,
    clearProfileImage,
    logout,
    reloadUser,
    refreshUserRoles,
    setPhoneVerifiedInDb,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [currentUser, userId, userEmail, emailVerified, phoneVerified, userRoles, loading, profileImage, profileImageBlobUrl, profileImageLoading, fetchProfileImage, refreshProfileImage, updateProfileImageState, clearProfileImage]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };